import { Link, useLoaderData, useNavigate, useParams } from "@remix-run/react";
import cx from "classnames";
import lodash from "lodash";
import { ChevronRight } from "lucide-react";
import qs from "qs";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Swiper, SwiperSlide } from "swiper/react";
import Accordion from "~/components/Accordion";
import Collapse from "~/components/Collapse";
import Container from "~/components/Container";
import LogoBar from "~/components/LogoBar";
import Product from "~/components/Product";
import ProductActionBar from "~/components/ProductActionBar";
import Products from "~/components/Products";
import contents from "~/constants/contents";
import { setModalParams } from "~/containers/Modal";

const sSettings = {
  // Default parameters
  slidesPerView: 1.2,
  spaceBetween: 15,
  // Responsive breakpoints
  breakpoints: {
    // when window width is >= 320px
    320: {
      slidesPerView: 1.8,
      spaceBetween: 15,
    },
    // when window width is >= 480px
    480: {
      slidesPerView: 2.2,
      spaceBetween: 15,
    },
    // when window width is >= 640px
    640: {
      slidesPerView: 1.2,
      spaceBetween: 15,
    },
    // when window width is >= 640px
    768: {
      slidesPerView: 1.2,
      spaceBetween: 15,
    },
  },
};

function ServicesMobile({
  textQuery,
  setTextQuery,
  sortFilter,
  setSortFilter,
  productsMappedWithProviders,
  ranges,
}) {
  const { providers, cookie, user, params, service, latestCompletedProducts } =
    useLoaderData();
  const goBack = () => navigate(-1);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { sid } = useParams();

  const renderFaq = () => {
    if (!lodash.isEmpty(service.content.faq)) {
      return service.content?.faq.map((f) => (
        <Accordion key={f.q} title={f.q} description={f.a} />
      ));
    }

    return contents?.faq.map((f) => (
      <Accordion key={f.q} title={t(f.q)} description={t(f.a)} />
    ));
  };

  const renderLastCompletedOperation = () => {
    if (lodash.isEmpty(latestCompletedProducts.results)) return;

    return (
      <section className="flex flex-col gap-1">
        <div className="text-xs font-semibold text-zinc-500 px-5 uppercase">
          {t("latestCountriesWithReceivedSms")}
        </div>
        <div className="rounded-2xl overflow-hidden w-full">
          {latestCompletedProducts.results.map((product, index) => (
            <Product
              key={product._id}
              product={product}
              productsToShow={latestCompletedProducts.results}
              setShowAll={() => {}}
              index={index}
              showAll={true}
              ranges={ranges}
              i18n={i18n}
              t={t}
              cookie={cookie}
              params={params}
              user={user}
            />
          ))}
        </div>
      </section>
    );
  };

  return (
    <section className="pb-20">
      <section className="mt-[calc(env(safe-area-inset-top))] mb-[calc(env(safe-area-inset-bottom))]">
        <Container className="relative flex w-full flex-col gap-5 my-5 rounded-xl">
          <div className="flex justify-between items-center px-3">
            <LogoBar />
            <div className="rounded-full bg-[#a8efc0] px-4 py-2 uppercase text-sm font-bold">
              {!cookie?.token && <Link to="/login">{t("login")}</Link>}
              {cookie?.token && (
                <Link className="flex gap-1 items-center" to="/profile/charge">
                  <span>{cookie.user.balance}</span>
                  <span className="text-xs text-base-content/40 font-medium uppercase">
                    {t("coins")}
                  </span>
                </Link>
              )}
            </div>
          </div>
          <ProductActionBar
            service={service}
            goBack={goBack}
            textQuery={textQuery}
            setTextQuery={setTextQuery}
            sortFilter={sortFilter}
            setSortFilter={setSortFilter}
            i18n={i18n}
            t={t}
          />
        </Container>

        <div className="border-b border-opacity-50 mt-4" />

        <Container className="grid grid-cols-1 gap-4 bg-base-200 py-5 px-5">
          <Collapse>
            <div className="flex flex-col gap-8">
              <h1 className="text-neutral-900 text-lg font-bold font-['Hubot-Sans','IRANYekanXVF']">
                {service.content?.title ||
                  t("buyVirtualNumbersFor", {
                    name: lodash.capitalize(service.name.common),
                  })}
              </h1>
              {service.media?.feature && (
                <img
                  src={`${service.media.feature}?format=webp&with=1000&height=500`}
                  alt={service.name.common}
                  className="rounded-2xl w-full object-cover"
                />
              )}
              <h2
                className="text-neutral-900 text-base font-normal font-['Figtree', 'IRANYekanXVF'] leading-7"
                dangerouslySetInnerHTML={{
                  __html: service.content?.description || t("home.description"),
                }}
              />

              <section className="-mx-5">
                <Swiper {...sSettings} centeredSlides autoplay loop>
                  {contents.benefits.map((b) => {
                    if (
                      i18n.language !== "fa" &&
                      b.name === "home.benefits.visaAndCreditCardAccepted"
                    )
                      return null;
                    return (
                      <SwiperSlide key={b.name}>
                        <div className="h-40 p-5 bg-[#e0f7e8] rounded-lg flex-col justify-center items-center gap-5 flex">
                          <div className="p-3 bg-[#c2efd1] rounded-full justify-center items-center gap-2 flex">
                            <b.Icon />
                          </div>
                          <div className="text-center text-neutral-900 text-base font-semibold font-['Hubot-Sans', 'IRANYekanXVF'] leading-normal">
                            {t(b.name)}
                          </div>
                        </div>
                      </SwiperSlide>
                    );
                  })}
                </Swiper>
              </section>

              <div className="flex flex-col gap-4">
                <h3 className="text-neutral-900 text-lg uppercase font-bold font-['Hubot-Sans', 'IRANYekanXVF']">
                  {t("faq")}
                </h3>

                <div className="flex items-start gap-6">
                  <div className="z-10 flex flex-col gap-3 w-full">
                    {renderFaq()}
                  </div>
                </div>
              </div>
            </div>
          </Collapse>

          {renderLastCompletedOperation()}

          <div className="rounded-2xl overflow-hidden w-full">
            <button
              className="flex justify-start bg-base-100 ps-4 gap-3 w-full items-center disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => {
                if (cookie) {
                  dispatch(
                    setModalParams({
                      target: "autoOrderConfirmation",
                      props: {
                        params,
                        cookie,
                        user,
                        service,
                        providers,
                      },
                    }),
                  );
                } else {
                  navigate(
                    "/login?" + qs.stringify({ next: "/services/" + sid }),
                  );
                }
              }}
            >
              <div className="flex justify-start items-center">
                <div className="h-8 w-8 rounded-full image-full bg-center bg-cover bg-no-repeat border fi-ir" />
                <div className="h-8 w-8 -ms-3 rounded-full image-full bg-center bg-cover bg-no-repeat border fi-in" />
                <div className="h-8 w-8 -ms-3 rounded-full image-full bg-center bg-cover bg-no-repeat border fi-jp" />
                <div className="h-8 w-8 -ms-3 rounded-full image-full bg-center bg-cover bg-no-repeat border fi-us" />
              </div>

              <div className="flex justify-between items-center grow border-b border-slate-200/50 pe-6">
                <div className="flex flex-col gap-1 py-3 items-start relative">
                  <div className="font-semibold capitalize block whitespace-nowrap overflow-ellipsis overflow-x-hidden max-w-48 md:max-w-52 xl:max-w-56">
                    {t("automatic")}
                  </div>
                  <div className="text-neutral-500 text-xs font-medium font-['Figtree', 'IRANYekanXVF'] leading-none flex gap-1 items-center">
                    {t("letHidSimFind")}
                  </div>
                </div>
                <div className="flex gap-1">
                  <div
                    className={cx(
                      "items-center justify-center text-xs leading-none flex shrink rounded-full px-2.5 py-1.5 bg-emerald-100 text-emerald-700",
                    )}
                  >
                    {t("floating")}
                  </div>
                  <div className="flex justify-center items-center">
                    <ChevronRight
                      size={18}
                      className={cx({
                        "rotate-180": i18n.dir() === "rtl",
                      })}
                    />
                  </div>
                </div>
              </div>
            </button>
          </div>

          {productsMappedWithProviders.map((p) => {
            return (
              <section className="flex flex-col gap-1" key={p.provider._id}>
                <div className="text-xs font-semibold text-zinc-500 px-5 uppercase">
                  {t(`providers.${p.provider.name.common}`)}
                </div>
                <div className="rounded-2xl overflow-hidden w-full">
                  <Products
                    products={p.products}
                    params={params}
                    cookie={cookie}
                    user={user}
                    r1={ranges[0]}
                    r2={ranges[1]}
                    r3={ranges[2]}
                  />
                </div>
              </section>
            );
          })}
        </Container>
      </section>
    </section>
  );
}

export default ServicesMobile;
